import { calculateBackgroundCheckStatus } from '@/backend/features/background-check/entities/background-check/utils/calculateBackgroundCheckStatus';
import { BackgroundCheckColumns, backgroundCheckEntityName } from '@/backend/features/background-check/shared';
import { getEnabledBackgroundCheckTypes } from '@/backend/features/background-check/shared/getEnabledBackgroundCheckTypes';
import { BackgroundCheckType } from '@/backend/features/background-check/types';
import {
    getBackgroundCheckConfigSettingProcessValue,
    isBackgroundCheckConfigUpdated,
} from '@/backend/features/background-check/utils/backgroundCheckTypeChanges';
import { getBackgroundCheckStorage } from '@/backend/features/background-check/utils/getBackgroundCheckStorage';
import { triggerRecalculateBackgroundCheckStatus } from '@/backend/features/background-check/utils/triggerRecalculateBackgroundCheckStatus';
import { ExtendedAccountColumns } from '@/backend/features/contact-management/entities/extended-account/types';
import { getExtendedAccountStorage } from '@/backend/features/contact-management/utils/getExtendedAccountStorage';
import { defineSettingMap } from '@juvare/platform-sdk-react/backend/features/editable-settings/shared';
import { Filter, FilterIdSubFilter } from '@juvare/platform-shared/features/storage';
import {
    BackgroundCheckConfig,
    backgroundCheckQuotaForm,
    backgroundCheckTestForm,
    defaultBackgroundCheckConfig,
    defaultBackgroundCheckQuota,
    defaultBackgroundCheckTest,
} from './defaults';

export const backgroundCheckSettingMap = defineSettingMap({
    backgroundCheckConfig: {
        type: 'json',
        description: 'Background Check Config',
        defaultValue: defaultBackgroundCheckConfig,
        group: 'system-admin',
        onScheduleChangedAsync: (_context, params) => {
            if (params.action === 'update') {
                return isBackgroundCheckConfigUpdated(params.prevData, params.data);
            }

            return false;
        },
        onChangedAsync: (context, params) => {
            const backgroundCheckStorage = getBackgroundCheckStorage(context);
            const accountStorage = getExtendedAccountStorage(context);

            if (params.action === 'update') {
                if (isBackgroundCheckConfigUpdated(params.prevData, params.data)) {
                    const config: BackgroundCheckConfig | undefined = params.data?.value ? JSON.parse(params.data.value) : undefined;

                    if (!config) {
                        return;
                    }

                    const enabledTypes: BackgroundCheckType[] = getEnabledBackgroundCheckTypes(config);

                    const enabledTypesSubFilter: Filter<ExtendedAccountColumns>[] = enabledTypes.map((enabledType) => ({
                        op: 'not-in',
                        idSubFilter: {
                            entity: backgroundCheckEntityName,
                            column: 'accountId',
                            filter: [
                                // We only need to address ones that are enabled
                                {
                                    column: 'type',
                                    op: '=',
                                    value: enabledType,
                                },
                            ],
                        } satisfies FilterIdSubFilter<BackgroundCheckColumns>['idSubFilter'],
                    }));

                    const accountsMissingBackgroundChecks = accountStorage.list({
                        filter: [
                            // Ignore deleted accounts
                            {
                                column: 'systemStatus',
                                op: '!=',
                                value: 'deleted',
                            },
                            {
                                op: 'or',
                                filter: enabledTypesSubFilter,
                            },
                        ],
                    }).rows;

                    const accountIdsMissingBackgroundChecks = accountsMissingBackgroundChecks?.map(({ id: accountId }) => accountId);

                    Object.keys(config.type).forEach((type) => {
                        if (!config.type[type as BackgroundCheckType]?.enabled) {
                            return;
                        }

                        // Check if record exists
                        const existingBackgroundChecksByType = backgroundCheckStorage.list({
                            filter: [
                                {
                                    column: 'type',
                                    op: '=',
                                    value: type,
                                },
                                {
                                    column: 'accountId',
                                    op: 'in',
                                    value: accountIdsMissingBackgroundChecks,
                                },
                            ],
                        }).rows;

                        // Create new records if they do not exist
                        accountsMissingBackgroundChecks.forEach((account) => {
                            const accountId = account.id;

                            // Create new records
                            const alreadyHasRecord = existingBackgroundChecksByType.some(
                                (backgroundCheck) => backgroundCheck.columns.accountId === accountId,
                            );

                            if (!alreadyHasRecord) {
                                const status = calculateBackgroundCheckStatus({
                                    backgroundCheckConfigForType: config.type[type as BackgroundCheckType],
                                });

                                backgroundCheckStorage.post({
                                    action: 'create',
                                    data: {
                                        accountId: accountId,
                                        status: status.status,
                                        type: type as BackgroundCheckType,
                                        updatedAt: status.updatedAt,
                                        consent: Boolean(account?.columns?.consentBackgroundCheck),
                                    },
                                });
                            }
                        });

                        triggerRecalculateBackgroundCheckStatus(
                            context,
                            getBackgroundCheckConfigSettingProcessValue(params.prevData, type as BackgroundCheckType),
                            getBackgroundCheckConfigSettingProcessValue(params.data, type as BackgroundCheckType),
                        );
                    });
                }
            }
        },
    },
    backgroundCheckQuota: {
        type: 'json',
        description: 'Background Check Quota',
        defaultValue: defaultBackgroundCheckQuota,
        group: 'system-admin',
        form: backgroundCheckQuotaForm,
    },
    backgroundCheckTest: {
        type: 'json',
        description: 'Background Check Test',
        defaultValue: defaultBackgroundCheckTest,
        group: 'system-admin',
        form: backgroundCheckTestForm,
    },
});

export type BackgroundCheckSettingMapType = typeof backgroundCheckSettingMap;
